<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useClickAway } from '@/utils/useClickAway';
import MyLoadsDesktopButtonComponent from '@/components/myLoads/MyLoadsDesktopButtonComponent.vue';
import EldExemptModalComponent from '@/components/Compliance/EldExemptModalComponent.vue';
import RadioButtonDesktopComponent from '@/components/RadioButtonDesktopComponent.vue';
import { useScreenSize } from '@/composables/useScreenSize';
import RadioButtonMobileComponent from '@/components/RadioButtonMobileComponent.vue';
import InputComponent from '@/components/InputComponent.vue';
import { useToast } from 'vue-toast-notification';
// import { getStopDay, getTimeFromNow } from '@/utils/time';

const toast = useToast();
const props = defineProps({
  externalLoadId: {
    type: String,
    required: true,
  },
  origin: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  destination: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  actionType: {
    type: String,
    required: true,
  },
  assignedVehicle: {
    type: Object,
    required: true,
    default: () => ({
      id: '',
      truck_number: '',
      vin: '',
    }),
  },
});

const { screenSize } = useScreenSize();

const isDesktop = computed(() => screenSize.value === 'desktop');

const store = useStore();

const isLoading = computed(() => store.state.myLoadsStore.vehicles.isLoading);

const allVehicles = computed(() =>
  store.state.myLoadsStore.vehicles.allVehicles.filter(
    (vehicle) =>
      !vehicle.eldExemptCategory?.includes('pending') &&
      !vehicle.eldExemptCategory?.includes('denied')
  )
);

const hasAllVehiclesLoaded = computed(
  () => store.state.myLoadsStore.vehicles.hasLoaded
);

const fetchData = () => {
  store.dispatch('myLoadsStore/getCarrierVehicles');
};

if (
  !allVehicles.value?.length &&
  !isLoading.value &&
  !hasAllVehiclesLoaded.value
) {
  fetchData();
}

const showInput = ref(!props.assignedVehicle.id);
const filteredVehicles = ref([]);
const vehicleInput = ref('');
watch(
  () => allVehicles.value,
  (newVal) => {
    filteredVehicles.value = newVal;
    vehicleInput.value = '';
  },
  { immediate: true, deep: true }
);
const modalRef = ref(null);
const emit = defineEmits([
  'closeModal',
  'assignVehicle',
  'openViewEquipmentModal',
  'confirmVehicle',
]);
useClickAway(modalRef, () => emit('closeModal'));
const handleVehicleInputChange = (value) => {
  vehicleInput.value = value;
  filteredVehicles.value = allVehicles.value.filter(
    ({ identifier, vin }) =>
      identifier
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(value.toString().toLowerCase().replace(/\s+/g, '')) ||
      vin
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(value.toString().toLowerCase().replace(/\s+/g, ''))
  );
};
const handleEnterPress = () => {
  if (filteredVehicles.value.length === 1) {
    const vehicle = filteredVehicles.value[0];
    if (vehicle.id === selectedVehicle.value?.id) {
      handleAssignVehicleClick();
      return;
    }
    handleRadioButtonClick(vehicle);
  }
};
const handleRadioButtonClick = (vehicle) => {
  selectedVehicle.value = {
    id: vehicle.id,
    truck_number: vehicle.identifier,
    vin: vehicle.vin,
    isEldActive: vehicle.isEldActive,
    isEldExempt: vehicle.isEldExempt,
  };
};
const isOpen = ref(true);
const handleConfirmVehicleClick = () => {
  isOpen.value = false;
  emit(
    'confirmVehicle',
    props.assignedVehicle.id,
    props.assignedVehicle.truck_number
  );
};

const handleChangeVehicleClick = () => {
  isOpen.value = true;
  showInput.value = true;
};

const handleAssignVehicleClick = () => {
  if (!selectedVehicle.value?.id) {
    toast.open({
      message: 'Please select a vehicle to assign.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  if (
    !selectedVehicle.value.isEldActive &&
    !selectedVehicle.value.isEldExempt
  ) {
    isEldExemptModalVisible.value = true;
    return;
  }
  emit(
    'assignVehicle',
    selectedVehicle.value.id,
    selectedVehicle.value.truck_number
  );
};

const selectedVehicle = ref(null);
const isEldExemptModalVisible = ref(false);
const isLoadingExemptStatusUpdate = ref(false);

const handleSetEldExemptStatusClick = async (exemptReason) => {
  isLoadingExemptStatusUpdate.value = true;
  isEldExemptModalVisible.value = false;
  if (!selectedVehicle.value?.id || !exemptReason?.id) {
    toast.open({
      message: 'Unable to update ELD exempt status. Please try again.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  const payload = {
    carrier_vehicle_id: selectedVehicle.value.id,
    eld_exempt_category: exemptReason.id,
  };
  let res = null;
  try {
    res = await store.dispatch(
      'myLoadsStore/postCarrierVehicleEldExemptStatus',
      { payload }
    );
  } catch (error) {
    isLoadingExemptStatusUpdate.value = false;
    toast.open({
      message: `Error: ${error.status == 403 ? 'Not Authorized to update ELD exempt status' : 'Unable to update ELD exempt status. Please try again.'}`,
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  if ([200, 201, 204].includes(res.status)) {
    isOpen.value = false;
    // emit(
    //   'assignVehicle',
    //   selectedVehicle.value.id,
    //   selectedVehicle.value.identifier
    // );
    toast.open({
      message:
        res.message ||
        `Carrier vehicle ${selectedVehicle.value.truck_number || ''} eld exempt status saved!`,
      position: 'top-right',
      // type: 'info',
      duration: 1500,
    });
  } else {
    isLoadingExemptStatusUpdate.value = false;
    toast.open({
      message: 'Unable to update ELD exempt status. Please try again.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
  }
};
</script>

<template>
  <div
    ref="modalRef"
    class="flex min-h-screen w-screen flex-1 items-center justify-center p-4"
  >
    <EldExemptModalComponent
      v-if="isEldExemptModalVisible"
      :selected-vehicle="selectedVehicle"
      :is-equipment-table="false"
      @close-modal="isEldExemptModalVisible = false"
      @set-eld-exempt-status-click="handleSetEldExemptStatusClick"
    />
    <div
      v-if="!isEldExemptModalVisible"
      class="flex w-full max-w-fit flex-1 flex-col gap-6 rounded-xl bg-white p-6 shadow-[0px_0px_1px_0px_rgba(31,36,46,0.31),0px_8px_12px_0px_rgba(31,36,46,0.16)]"
    >
      <div class="flex w-full flex-col gap-4">
        <div class="flex w-full items-center justify-between gap-4">
          <div class="flex gap-2">
            <div
              class="flex-1 justify-start text-xl font-medium leading-7 text-steel-1100"
            >
              {{ assignedVehicle?.id ? 'Confirm' : 'Assign' }} Vehicle
            </div>
          </div>
          <MyLoadsDesktopButtonComponent
            button-class="subtle-button rounded-[5px]"
            button-type="button"
            @button-click="$emit('closeModal')"
          >
            <template #leftIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M18.3639 7.05044L16.9497 5.63623L11.9999 10.586L7.0502 5.63623L5.63599 7.05044L10.5857 12.0002L5.63599 16.9499L7.0502 18.3642L11.9999 13.4144L16.9497 18.3642L18.3639 16.9499L13.4141 12.0002L18.3639 7.05044Z"
                  fill="#6C7C9D"
                />
              </svg>
            </template>
          </MyLoadsDesktopButtonComponent>
        </div>
        <div class="flex flex-col gap-6">
          <div
            class="flex max-w-fit flex-col gap-3 overflow-scroll rounded-[5px] bg-steel-100 p-5"
          >
            <div class="flex max-w-fit gap-10">
              <div
                v-if="!assignedVehicle?.id"
                class="flex flex-col gap-1"
              >
                <div class="text-xs font-semibold leading-none text-steel-800">
                  Load ID
                </div>
                <div
                  :title="externalLoadId || '-'"
                  class="text-sm font-normal capitalize leading-tight text-steel-900"
                >
                  {{ externalLoadId || '-' }}
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div class="text-xs font-semibold leading-none text-steel-800">
                  Origin
                </div>
                <div
                  class="text-sm font-normal capitalize leading-tight text-steel-900"
                >
                  {{
                    origin?.city
                      ? `${origin?.city}${origin?.city === '-' ? '' : `, ${origin?.state}`}`
                      : '-'
                  }}
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div class="text-xs font-semibold leading-none text-steel-800">
                  Destination
                </div>
                <div
                  class="text-sm font-normal capitalize leading-tight text-steel-900"
                >
                  {{
                    destination?.city
                      ? `${destination?.city}${destination?.city === '-' ? '' : `, ${destination?.state}`}`
                      : '-'
                  }}
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div class="text-xs font-semibold leading-none text-steel-800">
                  Pickup Date
                </div>
                <div
                  class="text-sm font-normal capitalize leading-tight text-steel-900"
                >
                  {{
                    origin?.appointmentDay
                      ? `${origin?.appointmentDay}${origin?.appointmentTime ? ` • ${origin?.appointmentTime}` : ''}`
                      : '-'
                  }}
                </div>
              </div>
            </div>

            <div
              v-if="assignedVehicle?.id"
              class="flex max-w-fit gap-10"
            >
              <div class="flex flex-col gap-1">
                <div class="text-xs font-semibold leading-none text-steel-800">
                  Load ID
                </div>
                <div
                  :title="externalLoadId || '-'"
                  class="text-sm font-normal capitalize leading-tight text-steel-900"
                >
                  {{ externalLoadId || '-' }}
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div class="text-xs font-semibold leading-none text-steel-800">
                  Truck #
                </div>
                <div
                  class="text-sm font-normal capitalize leading-tight text-steel-900"
                >
                  {{ assignedVehicle.truck_number || '-' }}
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div class="text-xs font-semibold leading-none text-steel-800">
                  VIN
                </div>
                <div
                  class="text-sm font-normal capitalize leading-tight text-steel-900"
                >
                  {{ assignedVehicle.vin || '-' }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!isDesktop && assignedVehicle?.id"
          class="flex items-center justify-between gap-2 self-stretch"
        >
          <MyLoadsDesktopButtonComponent
            button-class="flex-1 default-button rounded-[3px] px-3 py-1.5"
            button-text="Change Vehicle"
            text-class=" text-sm font-medium leading-10 tracking-tight"
            @button-click="handleChangeVehicleClick"
          />
          <MyLoadsDesktopButtonComponent
            button-class="flex-1 primary-button rounded-[3px] px-3 py-1.5"
            button-text="Confirm Vehicle"
            text-class="text-sm font-medium leading-10 tracking-tight"
            @button-click="handleConfirmVehicleClick"
          />
        </div>
        <div
          v-else-if="assignedVehicle?.id"
          class="flex items-center justify-end gap-2 self-stretch"
        >
          <MyLoadsDesktopButtonComponent
            button-class="default-button rounded-[5px] px-3 py-1 h-8"
            text-class="font-medium text-sm leading-10 tracking-tight"
            button-text="Change Vehicle"
            @button-click="handleChangeVehicleClick"
          />
          <MyLoadsDesktopButtonComponent
            button-class="primary-button rounded-[5px] px-3 py-1 h-8"
            text-class="font-medium text-sm leading-10 tracking-tight"
            button-text="Confirm Vehicle"
            @button-click="handleConfirmVehicleClick"
          />
        </div>
        <div
          v-if="showInput"
          class="flex flex-col gap-2"
        >
          <!-- loading vehicles -->
          <div
            v-if="isLoading"
            class="mx-auto h-16 w-16 text-center"
          >
            <img
              class="loading relative w-full"
              src="@/assets/loading-v2-yellow.svg"
              alt="loading"
            />
          </div>
          <!-- input search -->
          <!-- picklist -->
          <button
            v-if="allVehicles.length"
            class="flex min-w-fit items-center justify-start font-['Poppins'] text-xs font-medium leading-none tracking-tight text-yellow-700 underline hover:text-yellow-800 active:text-yellow-900"
            @click="$emit('openViewEquipmentModal')"
          >
            I don't see my vehicle
          </button>
          <div
            v-if="
              !isLoading && hasAllVehiclesLoaded && allVehicles.length === 0
            "
            class="text-rose-800"
          >
            No vehicles found.
          </div>
          <div
            v-if="allVehicles.length"
            class="relative mt-1 flex h-full w-full flex-col gap-3"
            @click.stop
          >
            <InputComponent
              input-label="assignVehicleModal"
              wrapper-class="flex w-full text-xs items-center gap-1.5 p-2 my-2"
              placeholder="Search Vehicles"
              :value="vehicleInput"
              @input-change="handleVehicleInputChange"
              @enter-press="handleEnterPress"
            >
              <template #leftIcon>
                <img
                  class="pt-1"
                  src="@/assets/search-light-gray.svg"
                  alt="filters"
                />
              </template>
            </InputComponent>
            <ul
              v-if="isOpen && isDesktop"
              aria-labelledby="combobox-button"
              role="listbox"
              class="min-h-[100px] w-full overflow-scroll rounded-md bg-white py-1"
              :class="assignedVehicle?.id ? 'max-h-[200px]' : 'max-h-[400px]'"
            >
              <RadioButtonDesktopComponent
                v-for="vehicle in filteredVehicles"
                :key="vehicle.id"
                :item="vehicle.identifier || '-'"
                :subtext="vehicle.vin"
                name="selectedVehicleDropdown"
                :value="vehicle.id"
                :show="false"
                :show-checkmark="false"
                :show-border-on-hover="false"
                :checked="vehicle.id === selectedVehicle?.id"
                :handle-click="() => handleRadioButtonClick(vehicle)"
              />
            </ul>
            <ul
              v-else-if="isOpen && !isDesktop"
              aria-labelledby="combobox-button"
              role="listbox"
              class="max-h-[200px] min-h-[100px] w-full overflow-scroll rounded-md bg-white py-1"
            >
              <RadioButtonMobileComponent
                v-for="vehicle in filteredVehicles"
                :key="vehicle.id"
                :item="vehicle.identifier || '-'"
                :subtext="vehicle.vin"
                name="selectedVehicleDropdown"
                :value="vehicle.id"
                :show="false"
                :show-checkmark="false"
                :show-border-on-hover="false"
                :checked="vehicle.id === selectedVehicle?.id"
                :handle-click="() => handleRadioButtonClick(vehicle)"
              />
            </ul>
            <div
              v-if="!isDesktop"
              class="flex items-center justify-between gap-2 self-stretch"
            >
              <MyLoadsDesktopButtonComponent
                button-class="flex-1 default-button rounded-[3px] px-3 py-1.5"
                button-text="Cancel"
                text-class=" text-sm font-medium leading-10 tracking-tight"
                @button-click="$emit('closeModal')"
              />
              <MyLoadsDesktopButtonComponent
                button-class="flex-1 primary-button rounded-[3px] px-3 py-1.5"
                button-text="Assign"
                text-class="text-sm font-medium leading-10 tracking-tight"
                @button-click="handleAssignVehicleClick"
              />
            </div>
            <div
              v-else
              class="flex items-center justify-end gap-2 self-stretch"
            >
              <MyLoadsDesktopButtonComponent
                button-class="default-button rounded-[5px] px-3 py-1 h-8"
                text-class="font-medium text-sm leading-10 tracking-tight"
                button-text="Cancel"
                @button-click="$emit('closeModal')"
              />
              <MyLoadsDesktopButtonComponent
                button-class="primary-button rounded-[5px] px-3 py-1 h-8"
                text-class="font-medium text-sm leading-10 tracking-tight"
                button-text="Assign Vehicle"
                @button-click="handleAssignVehicleClick"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.show-scroll {
  overflow: scroll;
  scrollbar-width: 2px;
}
/* .show-scroll::-webkit-scrollbar {
  display: block;
  scrollbar-width: 2px;
} */
</style>
